import React from "react";
import useApi from "@/hooks/useApi";

/**
 * Velaro internal superadmin page: /admin/platform-setup
 * Documents all the steps Velaro must complete to enable each social channel.
 * NOT visible to customers. Direct URL access only.
 *
 * Covers: Meta (Facebook + Instagram), TikTok, WhatsApp
 * Also covers: Salesforce AppExchange, TikTok Partner Portal, Meta App Review
 */

interface Section {
  id: string;
  platform: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  steps: StepGroup[];
}

interface StepGroup {
  title: string;
  steps: string[];
  code?: string;
  warning?: string;
  link?: { label: string; url: string };
}

const SECTIONS: Section[] = [
  {
    id: "meta",
    platform: "Meta (Facebook + Instagram)",
    icon: <i className="fab fa-facebook text-white" aria-hidden="true" />,
    color: "text-blue-700",
    bgColor: "bg-blue-600",
    steps: [
      {
        title: "1. Meta Developer App (already exists)",
        steps: [
          "App ID: 2402527613449911 (see .env.sample / VITE_FACEBOOK_APP_ID)",
          "App Secret: stored in VITE_FACEBOOK_SECRET (do NOT commit to git — use Azure App Settings in prod)",
          "App dashboard: https://developers.facebook.com/apps/2402527613449911/dashboard/"
        ]
      },
      {
        title: "2. Add Instagram Permissions to the App",
        steps: [
          "In Meta App Dashboard → Add Product → Messenger (includes Instagram Messaging)",
          "Go to App Review → Permissions and Features",
          "Request: instagram_manage_messages, instagram_basic, pages_messaging, pages_show_list",
          "instagram_manage_messages requires App Review — submit a screencast showing the use case",
          "Review takes 5–10 business days. Use 'Test app' mode with your own IG accounts while waiting."
        ],
        warning: "instagram_manage_messages CANNOT be tested in development mode with other users' accounts. Only the app's developers/testers can use it before review approval."
      },
      {
        title: "3. Configure Instagram Webhooks",
        steps: [
          "Meta App Dashboard → Products → Messenger → Webhooks",
          "Callback URL (staging): https://velaro-messaging-api-staging.azurewebsites.net/Instagram/Webhook",
          "Callback URL (prod): https://velaro-messaging-api.azurewebsites.net/Instagram/Webhook",
          "Verify token: set InstagramWebhookVerifyToken in the velaro-messaging-api-staging App Settings (already set on staging)",
          "Subscribe to fields: messages, messaging_postbacks, messaging_seen",
          "NOTE: The webhook handler lives in velaro-messaging (not admin). Admin only stores the config."
        ],
        warning: "The webhook handler is live in velaro-messaging. InstagramWebhookVerifyToken is already set on staging. You still need to configure the Meta App webhook subscription to point to the staging URL above."
      },
      {
        title: "4. Staging Azure App Settings (velaro-admin-staging)",
        steps: [
          "VITE_FACEBOOK_APP_ID=2402527613449911  (already set)",
          "These live in client/.env as VITE_ vars — they are public (client-side only)",
          "No server-side Instagram secret needed in admin — the Page Access Token is per-customer"
        ],
        code: "# Azure Portal → velaro-admin-staging → Environment Variables\nVITE_FACEBOOK_APP_ID=2402527613449911"
      },
      {
        title: "5. Production Checklist",
        steps: [
          "Set VITE_FACEBOOK_APP_ID in velaro-admin-prod App Settings",
          "App Review must be approved for instagram_manage_messages before any customer can connect",
          "Test with an internal Velaro IG Business account first (site 1032)",
          "Verify webhook delivery in Meta's Events Manager after go-live"
        ]
      }
    ]
  },
  {
    id: "tiktok",
    platform: "TikTok for Business",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.75a8.19 8.19 0 0 0 4.78 1.52V6.84a4.85 4.85 0 0 1-1.01-.15z" />
      </svg>
    ),
    color: "text-slate-800",
    bgColor: "bg-black",
    steps: [
      {
        title: "1. Register as a TikTok Solution Provider",
        steps: [
          "Apply at: https://developers.tiktok.com/  — Create a developer account",
          "Create a new App → choose 'Login Kit' + 'DM API (Business Message API)'",
          "Velaro needs to be approved as a 'TikTok Solution Partner' for commercial DM access",
          "Partner portal: https://partners.tiktok.com/  — apply under 'Marketing Technology Partners'",
          "Timeline: Approval typically takes 2–4 weeks. Non-partners can only use sandbox mode."
        ],
        warning: "TikTok's DM API (Business Message API) is NOT available to regular developers. You MUST be an approved TikTok Solution Partner to access it for commercial use. Apply early."
      },
      {
        title: "2. Create the TikTok App",
        steps: [
          "developers.tiktok.com → My Apps → Create App",
          "App Type: Web",
          "Required scopes: user.info.basic, message.send, message.receive",
          "Add redirect URI (staging): https://admin-staging.velaro.com/tiktok/callback",
          "Add redirect URI (prod): https://admin.velaro.com/tiktok/callback",
          "Copy Client Key and Client Secret"
        ]
      },
      {
        title: "3. Set Azure App Settings",
        steps: [
          "Add to velaro-admin-staging App Settings:",
          "  TikTok:ClientKey = <Client Key from TikTok developer portal>",
          "  TikTok:ClientSecret = <Client Secret from TikTok developer portal>",
          "  TikTok:RedirectUri = https://admin-staging.velaro.com/tiktok/callback",
          "Add same settings to velaro-admin-prod when going live (different redirect URI)"
        ],
        code: "# Azure Portal → velaro-admin-staging → Environment Variables\nTikTok:ClientKey=YOUR_CLIENT_KEY_HERE\nTikTok:ClientSecret=YOUR_CLIENT_SECRET_HERE\nTikTok:RedirectUri=https://admin-staging.velaro.com/tiktok/callback"
      },
      {
        title: "4. Configure TikTok Webhooks (velaro-messaging side)",
        steps: [
          "TikTok developer portal → your app → Webhooks",
          "Callback URL (staging): https://velaro-messaging-api-staging.azurewebsites.net/TikTok/Webhook",
          "Callback URL (prod): https://velaro-messaging-api.azurewebsites.net/TikTok/Webhook",
          "Subscribe to events: direct_message, message_seen",
          "Add verify token to messaging App Settings: TikTokWebhookVerifyToken",
          "NOTE: Webhook handler must be implemented in velaro-messaging"
        ],
        warning: "TikTok requires HTTPS with a valid certificate and the webhook endpoint must respond within 5 seconds. Use async processing."
      },
      {
        title: "5. Token Refresh (automatic)",
        steps: [
          "Access tokens expire every 2 hours. Velaro stores the refresh token (valid 365 days).",
          "Add a background job in velaro-messaging to refresh tokens proactively (before expiry).",
          "Call POST https://open.tiktokapis.com/v2/oauth/token/ with grant_type=refresh_token",
          "When refresh token expires, the TikTokConfig.TokenValid flag will show 'Expired'",
          "Admin will see a 'Reconnect' button — customer must re-authorize"
        ]
      },
      {
        title: "6. Sandbox Testing (before partner approval)",
        steps: [
          "In TikTok developer portal → Sandbox, add test user TikTok accounts",
          "Only sandbox users can authorize the app before partner approval",
          "Test the full OAuth flow end-to-end with sandbox accounts",
          "Verify token storage, refresh, and config page display"
        ]
      }
    ]
  },
  {
    id: "salesforce",
    platform: "Salesforce AppExchange",
    icon: <i className="fab fa-salesforce text-white" aria-hidden="true" />,
    color: "text-blue-600",
    bgColor: "bg-blue-700",
    steps: [
      {
        title: "1. Become a Salesforce AppExchange Partner",
        steps: [
          "Apply at: https://appexchange.salesforce.com/partners",
          "Register as an ISV (Independent Software Vendor) partner",
          "Free tier: AppExchange listing only. ISV Partner: managed packages + revenue share.",
          "Security Review is MANDATORY before any paid listing. Takes 2–8 weeks."
        ]
      },
      {
        title: "2. Salesforce CRM Integration (in-app)",
        steps: [
          "Build a Salesforce Connected App in Salesforce Setup → App Manager",
          "OAuth flows: user-side (customer authorizes Velaro) OR service account (single org-wide token)",
          "Recommended: Customer-side OAuth — each customer connects their Salesforce org",
          "Scopes needed: api, refresh_token, offline_access",
          "Store in SalesforceConfigs table (same pattern as HubSpot, Dynamics)",
          "Build /integrations/salesforce admin page (see Dynamics for pattern)",
          "Velaro use cases: contact/lead lookup, case creation, chat transcript sync"
        ],
        link: { label: "Salesforce Connected App docs", url: "https://help.salesforce.com/s/articleView?id=sf.connected_app_create.htm" }
      },
      {
        title: "3. AppExchange Listing Requirements",
        steps: [
          "Security Review: automated scanning + manual review of your Salesforce app",
          "ISV license management via LMA (License Management App) — tracks installs",
          "AppExchange app must be a managed package installed into customer Salesforce orgs",
          "Alternative: List Velaro as a 'Solution' (non-installable listing) — no security review needed",
          "Priority: Build the CRM integration first (step 2), then pursue AppExchange listing"
        ],
        warning: "Salesforce AppExchange security review is intensive. Budget 2–3 months for review + remediation. A simple 'Solution' listing is much faster and still gives marketplace exposure."
      }
    ]
  },
  {
    id: "shopify-app-store",
    platform: "Shopify App Store",
    icon: <i className="fab fa-shopify text-white" aria-hidden="true" />,
    color: "text-green-700",
    bgColor: "bg-green-600",
    steps: [
      {
        title: "App Store listing status",
        steps: [
          "Velaro already has a Shopify integration (BigCommerce + Shopify both ship in v20)",
          "To list on Shopify App Store: register at https://partners.shopify.com/",
          "Create a Shopify App (public) — our admin OAuth flow already exists",
          "Required: built-in billing via Shopify Billing API (customers pay through Shopify)",
          "Required: pass Shopify App Store review (UX, security, performance checklist)",
          "Listing gets ~300K+ merchant eyes — high ROI for discovery"
        ],
        link: { label: "Shopify Partner Portal", url: "https://partners.shopify.com/" }
      }
    ]
  },
  {
    id: "bigcommerce",
    platform: "BigCommerce App Marketplace",
    icon: <span className="text-white font-bold text-xs">BC</span>,
    color: "text-indigo-700",
    bgColor: "bg-indigo-600",
    steps: [
      {
        title: "App Marketplace listing status",
        steps: [
          "Velaro already has a BigCommerce integration in v20",
          "To list: register at https://developer.bigcommerce.com/",
          "Create a public app — our OAuth flow (BigCommerceCreate) already handles authorization",
          "Submit app for review via BigCommerce Developer Portal",
          "Listing gives access to ~60K+ merchants"
        ],
        link: { label: "BigCommerce Developer Portal", url: "https://developer.bigcommerce.com/" }
      }
    ]
  },
  {
    id: "maintenance",
    platform: "Ongoing Maintenance & Monitoring",
    icon: <i className="fas fa-tools text-white" aria-hidden="true" />,
    color: "text-slate-700",
    bgColor: "bg-slate-600",
    steps: [
      {
        title: "Token Health Monitoring",
        steps: [
          "TikTok: refresh tokens expire 365 days from issue. Monitor TikTokConfigs.RefreshTokenExpiresAt.",
          "Instagram: Page Access Tokens are long-lived (60 days) but can be invalidated if user changes password",
          "Add a Hangfire job in velaro-messaging to check expiring tokens weekly and send admin alerts",
          "Webhook health: set up an Azure Alert on HTTP 500 errors from webhook endpoints"
        ]
      },
      {
        title: "Meta App Review Renewals",
        steps: [
          "Meta may revoke permissions if your app violates Platform Policies",
          "Review Meta's usage policies annually: https://developers.facebook.com/policy/",
          "Data deletion callback: Meta requires a data deletion endpoint — implement DELETE /api/meta/data-deletion",
          "Privacy Policy URL must be kept up to date in the Meta App Dashboard"
        ]
      },
      {
        title: "TikTok Partner Renewal",
        steps: [
          "TikTok Solution Partner status must be renewed annually",
          "Maintain integration quality metrics (uptime, response time) as required by TikTok",
          "Monitor TikTok API changelog: https://developers.tiktok.com/blog/"
        ]
      },
      {
        title: "Customer Support Runbook",
        steps: [
          "Instagram DMs not coming through: check webhook subscription in Meta Events Manager",
          "TikTok token expired: customer clicks Reconnect on /tiktok page and re-authorizes",
          "New customer can't connect Instagram: verify EnableInstagram flag is on (DatabaseTool)",
          "TikTok connection fails: verify TikTok:ClientKey + ClientSecret are set in App Settings",
          "Meta permission denied: user's FB account may not be page admin — they need Page Manager role"
        ]
      }
    ]
  }
];

interface CredentialStatus {
  instagram: { webhookTokenConfigured: boolean; webhookUrl: string; webhookTokenHint?: string };
  tikTok: { clientKeyConfigured: boolean; clientSecretConfigured: boolean; redirectUriConfigured: boolean; redirectUri?: string; webhookUrl: string; clientKeyHint?: string };
  environment: string;
  allInstagramReady: boolean;
  allTikTokReady: boolean;
}

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
      <i className={`fas ${ok ? "fa-check-circle" : "fa-times-circle"}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function LiveStatusPanel() {
  const api = useApi();
  const [status, setStatus] = React.useState<CredentialStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    api.messaging.get("SuperAdmin/channel-credential-status")
      .then((r: Response) => r.json())
      .then((d: CredentialStatus) => setStatus(d))
      .catch(() => setError("Unable to load status — check that velaro-messaging is running and you are logged in as a Velaro admin."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500 py-2">Loading live status…</div>;
  if (error || !status) return <div className="text-sm text-red-600 py-2">{error ?? "Failed to load status."}</div>;

  return (
    <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-800">Live Credential Status</h2>
        <span className="text-xs text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
          env: {status.environment}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Instagram */}
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center">
              <i className="fab fa-instagram text-white text-xs" aria-hidden="true" />
            </span>
            <span className="font-medium text-slate-700">Instagram</span>
            <StatusBadge ok={status.allInstagramReady} label={status.allInstagramReady ? "Ready" : "Not ready"} />
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Webhook verify token</span>
              <StatusBadge ok={status.instagram.webhookTokenConfigured} label={status.instagram.webhookTokenConfigured ? "Set" : "Missing"} />
            </div>
            <div className="text-xs text-slate-400 font-mono break-all mt-1">
              Webhook URL: {status.instagram.webhookUrl}
            </div>
            {status.instagram.webhookTokenHint && (
              <div className="text-xs text-slate-400">Token: {status.instagram.webhookTokenHint}</div>
            )}
            {!status.instagram.webhookTokenConfigured && (
              <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1">
                Set <code className="font-mono">InstagramWebhookVerifyToken</code> in velaro-messaging App Settings
              </div>
            )}
          </div>
        </div>

        {/* TikTok */}
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-black flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.75a8.19 8.19 0 0 0 4.78 1.52V6.84a4.85 4.85 0 0 1-1.01-.15z" />
              </svg>
            </span>
            <span className="font-medium text-slate-700">TikTok</span>
            <StatusBadge ok={status.allTikTokReady} label={status.allTikTokReady ? "Ready" : "Not ready"} />
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Client Key</span>
              <StatusBadge ok={status.tikTok.clientKeyConfigured} label={status.tikTok.clientKeyConfigured ? "Set" : "Missing"} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Client Secret</span>
              <StatusBadge ok={status.tikTok.clientSecretConfigured} label={status.tikTok.clientSecretConfigured ? "Set" : "Missing"} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Redirect URI</span>
              <StatusBadge ok={status.tikTok.redirectUriConfigured} label={status.tikTok.redirectUriConfigured ? "Set" : "Missing"} />
            </div>
            {status.tikTok.redirectUri && (
              <div className="text-xs text-slate-400 font-mono break-all">→ {status.tikTok.redirectUri}</div>
            )}
            <div className="text-xs text-slate-400 font-mono break-all mt-1">
              Webhook URL: {status.tikTok.webhookUrl}
            </div>
            {(!status.tikTok.clientKeyConfigured || !status.tikTok.clientSecretConfigured) && (
              <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1">
                Set <code className="font-mono">TikTok:ClientKey</code> + <code className="font-mono">TikTok:ClientSecret</code> in velaro-messaging App Settings after getting credentials from TikTok Developer Portal
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-400 bg-white border border-slate-100 rounded px-3 py-2">
        <strong>Production note:</strong> These App Settings must be set separately in <code className="font-mono">velaro-messaging-api</code> (production). They are NOT copied automatically. TikTok:RedirectUri for prod = <code className="font-mono">https://admin.velaro.com/tiktok/callback</code>
      </div>
    </div>
  );
}

export default function PlatformChannelSetup() {
  const [activeSection, setActiveSection] = React.useState<string | null>(null);

  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Platform Channel Setup</h1>
        <p className="text-slate-500 mt-1">
          Velaro-internal setup and maintenance guide for social channel integrations.
          This page is not visible to customers — access via direct URL only.
        </p>
        <LiveStatusPanel />
        <div className="mt-3 flex flex-wrap gap-2">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() =>
                setActiveSection(activeSection === s.id ? null : s.id)
              }
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeSection === s.id
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full ${s.bgColor} flex items-center justify-center text-xs`}
              >
                {s.icon}
              </span>
              {s.platform}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {SECTIONS.filter((s) => !activeSection || s.id === activeSection).map((section) => (
          <div
            key={section.id}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          >
            {/* Section header */}
            <div className={`flex items-center gap-3 px-5 py-4 border-b border-slate-100`}>
              <div
                className={`w-8 h-8 rounded-full ${section.bgColor} flex items-center justify-center text-sm`}
              >
                {section.icon}
              </div>
              <h2 className="text-lg font-semibold text-slate-800">{section.platform}</h2>
            </div>

            {/* Step groups */}
            <div className="divide-y divide-slate-100">
              {section.steps.map((group, gi) => (
                <div key={gi} className="px-5 py-4">
                  <h3 className="font-semibold text-slate-700 mb-3">{group.title}</h3>

                  {group.warning && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                      <div className="flex items-start gap-2">
                        <i
                          className="fas fa-triangle-exclamation text-amber-500 mt-0.5 flex-shrink-0"
                          aria-hidden="true"
                        />
                        <p className="text-sm text-amber-800">{group.warning}</p>
                      </div>
                    </div>
                  )}

                  <ul className="space-y-1.5">
                    {group.steps.map((step, si) => (
                      <li key={si} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                          {si + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>

                  {group.code && (
                    <pre className="mt-3 bg-slate-900 text-green-400 rounded-lg px-4 py-3 text-xs overflow-x-auto font-mono">
                      {group.code}
                    </pre>
                  )}

                  {group.link && (
                    <a
                      href={group.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      {group.link.label}
                      <i className="fas fa-external-link-alt text-xs" aria-hidden="true" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
